import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import * as fromRoot from '@core/redux/index';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

/**
 * Маршруты для этапов регистрации
 */
const REGISTRATION_ROUTES = {
  STEP_1: '/auth/sign-up/step-1',
  STEP_2: '/auth/sign-up/step-2',
  STEP_3: '/auth/sign-up/step-3',
  SIGN_IN: '/auth/sign-in',
} as const;

/**
 * Соответствие этапов авторизации и маршрутов
 */
const STEP_ROUTE_MAP = {
  personalData: REGISTRATION_ROUTES.STEP_1,
  security: REGISTRATION_ROUTES.STEP_2,
  questions: REGISTRATION_ROUTES.STEP_3,
} as const;

/**
 * Функциональный guard для проверки авторизации
 */
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const router = inject(Router);
  const store = inject<Store<fromRoot.State>>(Store);

  const isAuth$ = store.select(fromRoot.getIsAuth);
  const authStep$ = store.select(fromRoot.getStep);
  const currentUrl = state.url;

  return combineLatest([isAuth$, authStep$]).pipe(
    map(([isAuth, authStep]) => {
      // Проверка базовой авторизации
      if (!isAuth) {
        router.navigate([REGISTRATION_ROUTES.SIGN_IN]);
        return false;
      }

      // Если авторизация завершена, разрешаем доступ
      if (authStep === 'end') {
        return true;
      }

      // Проверяем соответствие текущего этапа и URL
      const expectedRoute = STEP_ROUTE_MAP[authStep as keyof typeof STEP_ROUTE_MAP];

      if (expectedRoute && currentUrl !== expectedRoute) {
        router.navigate([expectedRoute]);
        return false;
      }

      return true;
    }),
    take(1)
  );
};
